import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import NoPermissionText from '@/components/atoms/NoPermissionText';
import BasicTabs from '@/components/molecules/BasicTabs';
import SideFilter from '@/components/molecules/SideFilter';
import TrainingFileModal from '@/components/molecules/TrainingFileModal';
import EmployeesChangesCard from '@/components/organisms/EmployeesChangesCard';
import { TrainingFilesTab } from '@/components/organisms/TrainingFilesTab';
import { Button } from '@/components/ui/button';
import { filters } from '@/constants/filters/TrainingFilesFilter';
import api from '@/services/apiSgft';
import { EmployeeChangesFiltersAtom } from '@/state/EmployeeChanges.atom';
import { pendingNotificationsCountAtom } from '@/state/PendingNotificationsCount.atom';
import { trainingFilesFiltersAtom } from '@/state/TrainingFilesFilter.atom';
import { userCanSeeTrainingControl } from '@/utils/handleSavePermissions';

const TrainingFiles = () => {
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const filterValues = useRecoilValue(trainingFilesFiltersAtom);
	const [pendingNotificationsCount, setPendingNotificationsCount] =
		useRecoilState(pendingNotificationsCountAtom);
	const [filteredData, setFilteredData] = useState(filterValues);
	const [currentTab, setCurrentTab] = useState(0);

	const filterEmployeeChangesValues = useRecoilValue(
		EmployeeChangesFiltersAtom,
	);

	// Fetch number of notifications that haven't been acknowledged yet
	// This number is shown in the modification notifications tab
	const fetchPendingNotificationsCount = async () => {
		try {
			const response = await api.get(
				'/training-modification-notification/pending-count',
				{
					params: {
						status: filterEmployeeChangesValues.status,
						employee: filterEmployeeChangesValues.employee,
						training: filterEmployeeChangesValues.trainings,
						type: filterEmployeeChangesValues.types,
					},
				},
			);
			return response.data;
		} catch (e) {
			return undefined;
		}
	};

	const { data: pendingNotificationsCountResponse } = useQuery(
		['pending-notifications-count', filterEmployeeChangesValues],
		fetchPendingNotificationsCount,
	);

	useEffect(() => {
		if (pendingNotificationsCountResponse !== undefined) {
			setPendingNotificationsCount(pendingNotificationsCountResponse);
		}
	}, [
		filterEmployeeChangesValues,
		pendingNotificationsCountResponse,
		setPendingNotificationsCount,
	]);

	const handleApplyFilter = (isReseting: boolean) => {
		if (isReseting) {
			setFilteredData(() => ({
				trainings: [],
				suppliers: [],
				status: [],
				timeRange: {
					endDate: undefined,
					startDate: undefined,
				},
				trainingFileNumber: [],
				trainingFileName: [],
				trainingType: [],
				responsible: [],
			}));
		} else {
			setFilteredData(() => ({
				...filterValues,
			}));
		}
	};

	const handleChangeTab = (_: React.SyntheticEvent, index: number) => {
		setCurrentTab(index);
	};

	if (!userCanSeeTrainingControl()) {
		return <NoPermissionText />;
	}
	return (
		<div className="relative flex h-full max-h-screen w-full flex-col items-start">
			<div className="absolute -top-8 right-20 flex items-center text-white ">
				<Button
					onClick={() => setIsCreateModalOpen(true)}
					type="button"
					className="h-8 rounded-full text-xs font-medium text-white"
				>
					Cadastrar Ficheiro
				</Button>
			</div>
			{isCreateModalOpen && (
				<TrainingFileModal
					isOpen={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
				/>
			)}
			{currentTab !== 2 && (
				<SideFilter
					refetchOnChange
					filters={filters}
					atom={trainingFilesFiltersAtom}
					applyChanges={handleApplyFilter}
				/>
			)}
			<div className="max-h-[85vh] w-full">
				<BasicTabs
					onChange={handleChangeTab}
					background="transparent"
					tabsNames={[
						'Planejados',
						'Concluídos',
						<>
							Alterações de Colaboradores
							<span className="ml-2 rounded-[20%] bg-[#cad5ff] px-2 py-1 text-xs text-[#445fcb]">
								{pendingNotificationsCount}
							</span>
						</>,
					]}
					tabs={[
						<TrainingFilesTab
							key="training-files-planned"
							type="planned"
							filteredData={filteredData}
							enabled={currentTab === 0}
						/>,
						<TrainingFilesTab
							key="training-files-completed"
							type="completed"
							filteredData={filteredData}
							enabled={currentTab === 1}
						/>,
						<div
							key="training-files-employee-changes"
							className="flex w-full min-w-min flex-col gap-2 pt-2"
						>
							<EmployeesChangesCard />
						</div>,
					]}
				/>
			</div>
		</div>
	);
};

export default TrainingFiles;
